import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ScrollBox } from '~/components/blocks';
import { TracingReportPanel } from '~/components/partials/TracingReportPanel';
import {
  GetLineVisitorTracingReportsDocument,
  GetLineVisitorTracingReportsQuery,
  TracingReportFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { tracingReportPageInfoState } from '~/state/partials/tracingreport_panel/atoms';

type Props = {
  lineVisitorId: string;
};

const getTotalPage = (data: GetLineVisitorTracingReportsQuery) => {
  const tracingReports = getNode(data, 'LineVisitor')?.tracingReports;
  return tracingReports ? tracingReports.pagesCount : 1;
};

export const TracingReport = (props: Props) => {
  const { lineVisitorId } = props;
  const [pageInfo, setPageInfo] = useAtom(tracingReportPageInfoState);
  const [patientDetailDialog, setPatientDetailDialogState] = useAtom(patientDetailDialogState);
  const isOpen = patientDetailDialog.isOpen;

  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);

  const [getTracingReports, { data, loading }] = useLazyQuery(
    GetLineVisitorTracingReportsDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  const prevData = usePreviousValue(data);

  useEffect(() => {
    if (!data) return;
    setPageInfo((_state) => ({ ..._state, totalPage: getTotalPage(data) }));
    scrollRef.current?.toTop();
  }, [data, setPageInfo]);
  const currentData = data || prevData;
  const tracingReports: TracingReportFragment[] = useMemo(() => {
    let results: TracingReportFragment[] = [];
    const patient = getNode(currentData, 'LineVisitor');
    if (patient) {
      results = patient.tracingReports.nodes.map((n) => {
        return { id: n.id, createdAt: n.createdAt, data: n.data };
      });
    }

    return results;
  }, [currentData]);

  useEffect(() => {
    if (pageInfo.target == 'GuestPatient' && lineVisitorId && !isOpen) {
      getTracingReports({
        variables: {
          lineVisitorId: lineVisitorId,
          page: pageInfo.page,
          perPage: pageInfo.perPage,
        },
      });
    }
  }, [
    isOpen,
    getTracingReports,
    lineVisitorId,
    pageInfo.page,
    pageInfo.perPage,
    pageInfo.target,
    patientDetailDialog.isOpen,
  ]);

  const handleCreateTracingReport = useCallback(() => {
    if (lineVisitorId) {
      setPatientDetailDialogState((_state) => ({
        ..._state,
        patientId: lineVisitorId,
        isOpen: true,
        type: 'tracingreport' as TabType,
      }));
    }
  }, [lineVisitorId, setPatientDetailDialogState]);

  return (
    <TracingReportPanel
      onCreate={handleCreateTracingReport}
      loading={loading}
      tracingReports={tracingReports}
    />
  );
};
